/**
 * Cache mutation engine for proposal WebSocket messages.
 *
 * The frontend uses many cache-key permutations for proposal lists
 * (page, page_size, filter_tag, include_recipient_data). The naive
 * `setQueryData(proposalQueryKeys.list(orgId), ...)` only updates one
 * permutation, leaving stale list views elsewhere. This module enumerates
 * matching cache entries and applies per-key logic so a single received
 * proposal lands in every list cache where it would actually appear,
 * without polluting filtered or paginated views with mismatched rows.
 */

import type { QueryClient } from '@tanstack/react-query';

import type {
  ProposalDetail,
  ProposalListFilters,
  ProposalListItem,
  ProposalsListResponse,
} from '../core/types';
import { proposalQueryKeys } from '../query/keys';
import {
  bumpFilterTagCounts,
  extractFilters,
  mergeProposalEntry,
  proposalMatchesFilters,
  unbumpFilterTagCounts,
} from './cacheMergeHelpers';
import type { WSProposalCreated, WSProposalUpdated } from './types';

/**
 * Merge a fresh proposal into a paginated list response.
 *
 * - If the proposal is already present (by id), the existing entry is
 *   replaced in place. This preserves page positioning so the user
 *   doesn't see a row visually jump. Recipient-enriched fields from
 *   the existing entry are preserved when the WS payload narrows them
 *   to `null` (see `mergeProposalEntry`).
 * - If the proposal is new and the cache is the first page, it's
 *   prepended to the results and the total count is incremented.
 *   When `pageSize` is set, the trailing row that would now spill onto
 *   page 2 is dropped so the cached page stays at most `pageSize` long
 *   — matching what the server would return on a fresh fetch and avoiding
 *   transient pagination inconsistencies.
 * - If the proposal is new and the cache is a later page, only the
 *   count is incremented — the proposal belongs on page 1, not here.
 *   The next fetch repaginates correctly.
 * - On any insertion (first page or later), `filter_tag_counts` is
 *   incremented for each of the proposal's filter tags so counters
 *   stay aligned with the inserted row.
 */
function mergeProposalIntoList(
  list: ProposalsListResponse,
  proposal: ProposalListItem,
  isFirstPage: boolean,
  pageSize: number | undefined
): ProposalsListResponse {
  const existingIndex = list.results.findIndex(p => p.id === proposal.id);
  if (existingIndex >= 0) {
    const nextResults = [...list.results];
    nextResults[existingIndex] = mergeProposalEntry(
      list.results[existingIndex],
      proposal
    );
    return { ...list, results: nextResults };
  }

  const filter_tag_counts = bumpFilterTagCounts(
    list.filter_tag_counts,
    proposal
  );

  if (!isFirstPage) {
    return { ...list, count: list.count + 1, filter_tag_counts };
  }

  const prepended = [proposal, ...list.results];
  const cappedResults =
    pageSize && pageSize > 0 ? prepended.slice(0, pageSize) : prepended;

  return {
    ...list,
    count: list.count + 1,
    results: cappedResults,
    filter_tag_counts,
  };
}

/**
 * Apply a `proposal.created` event: insert the proposal into every list
 * cache entry for this org whose filters the proposal actually satisfies.
 *
 * Caches whose filters exclude the proposal are left untouched — inserting
 * there would briefly show wrong rows in filtered views (e.g. a non-email
 * proposal appearing in the "email" tab). Page-2+ caches receive a count
 * bump only; the row itself belongs on page 1.
 */
export function applyProposalCreated(
  queryClient: QueryClient,
  orgId: number,
  proposal: WSProposalCreated
): void {
  const cache = queryClient.getQueryCache();
  const matches = cache.findAll({ queryKey: proposalQueryKeys.lists(orgId) });

  for (const query of matches) {
    const list = query.state.data as ProposalsListResponse | undefined;
    if (!list || !Array.isArray(list.results)) {
      // Don't synthesize a list cache from nothing — that would create a
      // partial response (e.g., wrong filter_tag_counts). The next HTTP
      // fetch will populate this key correctly. Entries without a flat
      // `results` array are the history views' infinite caches
      // (InfiniteData under the same lists prefix) — invalidation keeps
      // those fresh; surgical merges only apply to flat responses.
      continue;
    }

    const filters = extractFilters(query.queryKey);
    if (!proposalMatchesFilters(proposal, filters)) {
      continue;
    }

    const isFirstPage = !filters?.page || filters.page === 1;
    queryClient.setQueryData<ProposalsListResponse>(
      query.queryKey,
      mergeProposalIntoList(list, proposal, isFirstPage, filters?.page_size)
    );
  }
}

/**
 * Merge a `proposal.updated` event into a paginated list response.
 *
 * Unlike `mergeProposalIntoList`, this never inserts a new row — an update
 * only ever patches or removes a proposal that's already present:
 * - Not present in this cache → left unchanged (updates never synthesize
 *   rows; the next fetch/create event is responsible for that).
 * - Present and still matches this cache's filters → replaced in place,
 *   preserving page positioning. Membership is unchanged, so `count` and
 *   `filter_tag_counts` are untouched.
 * - Present but no longer matches (e.g. resolution changed out of a
 *   filtered view) → removed, with `count` decremented and
 *   `filter_tag_counts` decremented for the removed row's tags.
 */
function mergeProposalUpdateIntoList(
  list: ProposalsListResponse,
  proposal: ProposalListItem,
  filters: ProposalListFilters | undefined
): ProposalsListResponse {
  const existingIndex = list.results.findIndex(p => p.id === proposal.id);
  if (existingIndex < 0) {
    return list;
  }

  if (proposalMatchesFilters(proposal, filters)) {
    const nextResults = [...list.results];
    nextResults[existingIndex] = mergeProposalEntry(
      list.results[existingIndex],
      proposal
    );
    return { ...list, results: nextResults };
  }

  const removed = list.results[existingIndex];
  const nextResults = [
    ...list.results.slice(0, existingIndex),
    ...list.results.slice(existingIndex + 1),
  ];
  return {
    ...list,
    results: nextResults,
    count: Math.max(0, list.count - 1),
    filter_tag_counts: unbumpFilterTagCounts(list.filter_tag_counts, removed),
  };
}

/**
 * Apply a `proposal.updated` event: patch every list cache entry where the
 * proposal is already present, and remove it from caches whose filters it
 * no longer matches (reject-side movement). Also merges the overlapping
 * scalar fields into the detail cache when present.
 *
 * Updates never insert — a proposal absent from a given cache is left
 * alone there, and a proposal absent from every cache (list and detail
 * alike) is a safe no-op.
 */
export function applyProposalUpdated(
  queryClient: QueryClient,
  orgId: number,
  proposal: WSProposalUpdated
): void {
  const cache = queryClient.getQueryCache();
  const matches = cache.findAll({ queryKey: proposalQueryKeys.lists(orgId) });

  for (const query of matches) {
    const list = query.state.data as ProposalsListResponse | undefined;
    // Skip undefined entries and the history views' infinite caches (no flat
    // `results` array) — see the matching guard in applyProposalCreated.
    if (!list || !Array.isArray(list.results)) {
      continue;
    }

    const filters = extractFilters(query.queryKey);
    const next = mergeProposalUpdateIntoList(list, proposal, filters);
    if (next !== list) {
      queryClient.setQueryData<ProposalsListResponse>(query.queryKey, next);
      // A reject-side removal (the proposal transitioned out of this filtered
      // list) leaves a paginated page with a visible hole: the row is gone but
      // the next server-side row that would fill it lives on a later page, and
      // local cache mutation can't pull it up. When more members remain beyond
      // this page (`count > results.length` after removal), invalidate so the
      // server repaginates and backfills. `invalidateQueries` refetches only the
      // active (mounted) query, so unmounted list caches stay lazy. A row was
      // removed only when `count` dropped; an in-place patch (count unchanged)
      // or a page that already holds all remaining members needs no refetch.
      if (next.count < list.count && next.count > next.results.length) {
        void queryClient.invalidateQueries({ queryKey: query.queryKey });
      }
      continue;
    }

    // `next === list` means the proposal is absent from this cache.
    // mergeProposalUpdateIntoList never inserts, so a proposal that TRANSITIONED
    // INTO this cache's membership filter (e.g. pending→approved into an
    // already-loaded approved-only list) would otherwise stay missing until an
    // unrelated refetch.
    //
    // Gate first on a MEMBERSHIP filter the proposal now satisfies. Absence
    // from an UNFILTERED list means the proposal sits on a later page
    // (pagination), NOT that a filter excluded it — inserting there would
    // wrongly hoist a later-page row onto page 1. Those rows are covered by the
    // `created` event and list refetches, not by this transition-in path.
    const hasMembershipFilter =
      !!filters &&
      (filters.resolution !== undefined ||
        filters.filter_tags__contains !== undefined ||
        filters.source_type_name !== undefined);
    if (!hasMembershipFilter || !proposalMatchesFilters(proposal, filters)) {
      continue;
    }

    // A membership filter isn't enough on its own: a FILTERED list can also be
    // paginated, and an absent-but-matching proposal may already be a member
    // sitting on a later page (ordinary pagination), not a genuine transition
    // in. Synthesizing an insert there would hoist a later-page row onto page 1
    // and double-count it. We can only tell absence proves non-membership when
    // this first-page cache already holds ALL of its filter's members
    // (`count <= results.length`); then an absent match must be new. Otherwise
    // the cache is paginated and we can't distinguish transition from
    // pagination locally — invalidate so the server repaginates authoritatively.
    const isFirstPage = !filters?.page || filters.page === 1;
    const holdsAllMembers = list.count <= list.results.length;
    if (isFirstPage && holdsAllMembers) {
      queryClient.setQueryData<ProposalsListResponse>(
        query.queryKey,
        mergeProposalIntoList(list, proposal, true, filters?.page_size)
      );
    } else {
      queryClient.invalidateQueries({ queryKey: query.queryKey });
    }
  }

  const detailKey = proposalQueryKeys.detail(orgId, proposal.id);
  const detail = queryClient.getQueryData<ProposalDetail>(detailKey);
  if (detail) {
    // The fast-mode list payload does not carry `execution_result` or other
    // detail-only fields. Merge all overlapping scalar fields from the payload
    // so the detail view stays consistent with list views after a resolution/
    // execution update. Excluded: recipient fields (primary_recipient_email,
    // associated_contact, associated_company) are always null in the fast-mode
    // payload — must not overwrite the detail's enriched copies. action_count
    // is also excluded: ProposalDetail uses the `actions` array, not
    // action_count.
    queryClient.setQueryData<ProposalDetail>(detailKey, {
      ...detail,
      title: proposal.title,
      description: proposal.description,
      reasoning_summary: proposal.reasoning_summary,
      resolution: proposal.resolution,
      assigned_user: proposal.assigned_user,
      originally_assigned_user: proposal.originally_assigned_user,
      resolved_by: proposal.resolved_by,
      resolved_at: proposal.resolved_at,
      read_at: proposal.read_at,
      filter_tags: proposal.filter_tags,
      source_type_name: proposal.source_type_name,
      source_id: proposal.source_id,
      created_at: proposal.created_at,
      updated_at: proposal.updated_at,
      root_entity_ids: proposal.root_entity_ids,
    });
    // The optimistic merge above patches only the overlapping scalar fields; the
    // WS payload does NOT carry `execution_result`, so on an execution-completion
    // update the open detail would otherwise keep stale execution data. Invalidate
    // with the default `refetchType: 'active'` so the currently-rendered detail
    // query refetches the authoritative record (execution_result included).
    // Only the mounted detail refetches — inactive/cached details stay lazy — so
    // this is one targeted request per open proposal on a resolution/execution
    // change, not a refetch storm.
    void queryClient.invalidateQueries({ queryKey: detailKey });
  }
}
